// Our base component, which all others extend.
import { OutlineLink } from '../../base/outline-link/outline-link';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import headingStyle from '../se-heading/se-heading.css.lit';
import linkStyle from '../se-link/se-link.css.lit';
import listStyle from '../se-list/se-list.css.lit';
import quoteStyle from '../se-pull-quote/se-pull-quote.css.lit';
import componentStyles from './se-wysiwyg.css.lit';
import { SlotController } from '../../controllers/slot-controller';
import '../../base/outline-icon/outline-icon';

type anchorLink = {
  instance: SeWysiwyg,
  link: HTMLElement,
  hash: string,
};

/**
 * The Saul-Ewing WYSIWYG component
 * @slot - The default, and only slot for this element.
 */
@customElement('se-wysiwyg')
export class SeWysiwyg extends OutlineLink {
  static styles: CSSResultGroup = [
    componentStyles,
    headingStyle,
    linkStyle,
    listStyle,
    quoteStyle,
  ];
  summary = document.createElement('div');
  fullContent = document.createElement('div');

  @property({ type: Boolean })
  readMoreFound: boolean;

  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );


  anchorLinks: Array<anchorLink> = [];

  firstUpdated(): void {
    this.getAnchors();
    this.wrapTables();
    this.convertReadMore();
    if (this.readMoreFound) {
      setTimeout(() => {
        this.setPanelHeight(0);
        this.createButton();
      }, 0);
    }
    setTimeout(() => {
      this.handleAnchor(window.location.hash);
    }, 0)
  }

  panelTemplate(): TemplateResult {
    return html` <button type="button" class="dropdown-trigger">
        <span class="dropdown-trigger-label"> Continue reading </span>
        <outline-icon
          slot="right"
          size="var(--fs-lg)"
          name="chevron-down"
          library="system"
          color="var(--gray-600)"
        ></outline-icon>
        <hr />
      </button>
      ${this.fullContent}`;
  }

  render(): TemplateResult {
    return html`
      ${this.summary}
      ${this.readMoreFound ? html` ${this.panelTemplate()}` : ''}
    `;
  }

  setPanelHeight(height: number | string) {
    this.fullContent.style.setProperty(
      '--fullHeight',
      `${height}${typeof height === 'number' ? 'px' : ''}`
    );
  }

  /**
   * Create read-more button
   */
  createButton() {
    const button = this.renderRoot.querySelector('button');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', 'panel');
      // add click event that switches the aria-expanded attribute
      button.addEventListener('click', () => {
        if (button.getAttribute('aria-expanded') === 'true') {
          button.setAttribute('aria-expanded', 'false');
          button.childNodes[1].textContent = 'Continue reading';
          button.children[1].setAttribute('name', 'chevron-down');

          // For height animation to work, set current height, and after minimal timeout set 0
          const currentHeight = this.fullContent.offsetHeight;
          this.setPanelHeight(currentHeight);
          setTimeout(() => {
            this.setPanelHeight(0);
            setTimeout(() => {
              this.fullContent.style.display = 'none';
            }, 500);
          }, 0);
        } else {
          button.setAttribute('aria-expanded', 'true');
          button.childNodes[1].textContent = 'Show less';
          button.children[1].setAttribute('name', 'chevron-up');

          // Setting height to auto to allow offsetHeight calculation of element
          this.fullContent.style.display = 'block';
          this.setPanelHeight('auto');
          const currentHeight = this.fullContent.offsetHeight;

          // For height animation to work, set height to 0, and after minimal timeout set the content-height
          this.setPanelHeight(0);
          setTimeout(() => {
            this.setPanelHeight(currentHeight);

            // after .5s (CSS transition value), reset CSS var to auto, so content fit when screen gets resized
            setTimeout(() => {
              // set correct panel height, in case the user double-clicked the button
              if (button.getAttribute('aria-expanded') !== 'false') {
                this.setPanelHeight('auto');
                this.fullContent.style.display = 'block';
              } else {
                this.setPanelHeight('0');
                this.fullContent.style.display = 'none';
              }
            }, 500);
          }, 0);
        }
      });
    }
  }

  /**
   * Create panel
   */
  createPanel(panel: HTMLElement) {
    panel.className = 'panel';
    panel.setAttribute('tabindex', '-1');
    return panel;
  }

  /**
   * Replace read-more placeholder with a button
   */
  convertReadMore() {
    const text = '[read-more]';

    // loop through all elements, filter only elements until read-more button is found, create a new nodelist for all elements afterwards
    this.readMoreFound = false;
    const elements: NodeListOf<HTMLElement> =
      this.querySelectorAll(':scope > *');
    this.createPanel(this.fullContent);
    elements.forEach(el => {
      if (!this.readMoreFound) {
        if (el.textContent!.toLowerCase().includes(text.toLowerCase())) {
          el.remove();
          this.readMoreFound = true;
        } else {
          this.summary.append(el);
        }
      } else {
        this.fullContent.append(el);
      }
    });
  }

  /**
   * Wrap table elements with a div
   */
  wrapTables() {
    const elements: NodeListOf<HTMLElement> =
      this.querySelectorAll(':scope > table');
    elements.forEach(el => {
      const tableWrapper = document.createElement('div');
      tableWrapper.className = 'se-wysiwyg-table-wrapper';
      const elCopy = el.cloneNode(true);
      el.insertAdjacentElement('afterend', tableWrapper);
      tableWrapper.append(elCopy);
      el.remove();
    });
  }

  /**
   * Get all anchor links so we can reinvent yet another standard html operation
   */
  getAnchors() {
    const anchors: NodeListOf<HTMLElement> = this.querySelectorAll('a');
    anchors.forEach(anchor => {
      if (anchor.getAttribute('href')?.startsWith('#')) {
        const hash = anchor.getAttribute('href') ?? '';

        anchor.addEventListener('click', () => {
          this.handleAnchor(hash);
        });

        this.anchorLinks.push({
          instance: this,
          link: anchor,
          hash: hash,
        });
      }
    });
  }

  handleAnchor(hash: string) {
    for (const anchor of this.anchorLinks) {
      if (hash && hash === anchor.hash) {
        const element = this.shadowRoot?.querySelector(hash);
        if (element) {
          element.scrollIntoView();
          window.scrollBy(0, -140);
        }
        break;
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-wysiwyg': SeWysiwyg;
  }
}
